// AdminFeedbackPage.js — Phase BRANCH-Q (2026-06-24)
//
// /admin/feedback — the cockpit for gradually walking top-rated mini-apps to a
// better place. Shows community feedback rolled up PER APP, prioritized by
// rating × actionable volume, classified (bug / visual / feature). Each app has
// a "Branch & Improve" action that enqueues a branch job to Firestore
// `branch_queue`; the admin's own Mac (BranchQueuePoller, scoped to
// requestedBy == uid) claims it and runs branchPublishedURL locally — fetch the
// published bundle → CLI branch against the feedback → install a 2.0 → review +
// publish. This is how the Meta-Harness slowly improves the golden binaries.
//
// Admin gate: same ADMIN_UIDS allowlist as the other /admin/* pages. The feed
// read + the queue write both go through admin-only Cloud Functions
// (adminListFeedback / adminQueueBranch), so the client never needs cross-user
// reads of feedback_tickets and the server validates the admin role.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

function formatDate(millis) {
  if (!millis) return '';
  const d = new Date(millis);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// Compact "3d ago" recency for at-a-glance scanning; the absolute date-time
// rides along in a title tooltip (formatDate). Returns '' for missing times so
// pre-timestamp tickets just render blank rather than "NaN ago".
function relativeTime(millis) {
  if (!millis) return '';
  const diff = Date.now() - millis;
  if (diff < 60_000) return 'just now';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

// ── Category facets (all derived client-side from data the function already
// returns) ────────────────────────────────────────────────────────────────
//
// Origin platform is encoded in each ticket's `source` as "{platform}.{surface}"
// — "ios.applet.snake", "macos.communityHubPlayer", "macos.aiFeed",
// "web.feedback". We parse the leading segment, falling back to the applet
// group's platform. Type splits native applets from published web mini-apps.

const PLATFORM_META = {
  ios:     { label: 'iOS',     bg: '#eef2ff', fg: '#3730a3' },
  macos:   { label: 'macOS',   bg: '#ecfeff', fg: '#155e75' },
  web:     { label: 'Web',     bg: '#f0fdf4', fg: '#166534' },
  android: { label: 'Android', bg: '#fef3c7', fg: '#92400e' },
};
const PLATFORM_ORDER = ['ios', 'macos', 'web', 'android'];

// Origin platform of one ticket. Native sources prefix the platform
// ("ios.applet.x", "macos.aiFeed"); the web product-feedback form instead sends
// an app-name or "unknown" source with platform:null. So for a non-applet
// (mini-app) group, an unresolved origin means the web form — default to 'web'
// rather than dropping the ticket out of the Platform filter. `appKind` is
// optional; without it the function stays a pure source/platform parse.
function ticketPlatform(t, appKind) {
  const head = String(t.source || '').toLowerCase().split('.')[0];
  if (PLATFORM_META[head]) return head;
  const p = String(t.platform || '').toLowerCase();
  if (PLATFORM_META[p]) return p;
  if (appKind && appKind !== 'applet') return 'web';
  return '';
}

const TYPE_META = {
  applet:  { label: 'Applet' },
  miniapp: { label: 'Mini-app (web)' },
  other:   { label: 'Other' },
};
const TYPE_ORDER = ['applet', 'miniapp', 'other'];

function appType(app) {
  if (app.kind === 'applet') return 'applet';
  if (app.branchable || app.slug) return 'miniapp';
  return 'other';
}

// Union of the platforms an app's tickets came from (a product can collect
// feedback from iOS, macOS and web players at once). Applet groups fall back to
// the group-level platform when no ticket carries a source prefix.
function appPlatforms(app) {
  const set = new Set();
  for (const t of app.tickets || []) {
    const p = ticketPlatform(t, app.kind);
    if (p) set.add(p);
  }
  if (set.size === 0) {
    const p = String(app.platform || '').toLowerCase();
    if (PLATFORM_META[p]) set.add(p);
  }
  return set;
}

function appTicketTimes(app) {
  const times = (app.tickets || [])
    .map((t) => t.whenMillis)
    .filter((m) => typeof m === 'number' && m > 0);
  if (times.length === 0) return { newest: null, oldest: null };
  return { newest: Math.max(...times), oldest: Math.min(...times) };
}

// Order tickets within a card by submission time, matching the chosen page
// direction ('oldest' ascending, otherwise newest-first). Missing times sink.
function sortTickets(tickets, direction) {
  return [...tickets].sort((a, b) => {
    const am = a.whenMillis, bm = b.whenMillis;
    if (am == null && bm == null) return 0;
    if (am == null) return 1;
    if (bm == null) return -1;
    return direction === 'oldest' ? am - bm : bm - am;
  });
}

const SORT_OPTIONS = [
  { id: 'newest',   label: 'Newest first' },
  { id: 'oldest',   label: 'Oldest first' },
  { id: 'priority', label: 'Priority' },
];

const FILTER_PREFS_KEY = 'adminFeedback.prefs.v1';

function loadPrefs() {
  try {
    const raw = window.localStorage.getItem(FILTER_PREFS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return {
      sort: SORT_OPTIONS.some((o) => o.id === p.sort) ? p.sort : 'newest',
      types: Array.isArray(p.types) ? p.types.filter((x) => TYPE_META[x]) : [],
      platforms: Array.isArray(p.platforms) ? p.platforms.filter((x) => PLATFORM_META[x]) : [],
    };
  } catch {
    return null;
  }
}

function savePrefs(prefs) {
  try {
    window.localStorage.setItem(FILTER_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* private mode / quota — filtering still works, just not remembered */
  }
}

// Classification → chip colour. The Swift/cloud triage emits OBVIOUS_BUG,
// VISUAL_BUG, FEATURE_REQUEST, AMBIGUOUS; tickets may also be unclassified.
function clsStyle(cls) {
  const c = String(cls || '').toUpperCase();
  if (c.includes('BUG') && c.includes('VISUAL')) return { bg: '#fef3c7', fg: '#92400e', label: 'visual' };
  if (c.includes('BUG')) return { bg: '#fee2e2', fg: '#991b1b', label: 'bug' };
  if (c.includes('FEATURE')) return { bg: '#dbeafe', fg: '#1e40af', label: 'feature' };
  if (c.includes('AMBIG')) return { bg: '#f3e8ff', fg: '#6b21a8', label: 'ambiguous' };
  return { bg: '#f3f4f6', fg: '#4b5563', label: c ? c.toLowerCase() : 'unclassified' };
}

const STATUS_STYLE = {
  pending:   { bg: '#e0e7ff', fg: '#3730a3', label: 'Queued' },
  running:   { bg: '#fef9c3', fg: '#854d0e', label: 'Branching…' },
  done:      { bg: '#dcfce7', fg: '#166534', label: 'Done' },
  failed:    { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
  cancelled: { bg: '#f3f4f6', fg: '#6b7280', label: 'Cancelled' },
};

const CLOSED_TICKET_STATUSES = new Set(['closed', 'resolved']);
const CLOSED_REVIEW_STATUSES = new Set(['rejected', 'resolved']);

function ticketIsClosed(ticket) {
  const status = String(ticket.status || 'open').toLowerCase();
  const reviewStatus = String(ticket.reviewStatus || 'queued').toLowerCase();
  const ownerDecision = String(ticket.ownerDecision || '').toLowerCase();
  return (
    CLOSED_TICKET_STATUSES.has(status) ||
    CLOSED_REVIEW_STATUSES.has(reviewStatus) ||
    ownerDecision === 'reject' ||
    ownerDecision === 'resolve'
  );
}

function ticketStatusLabel(ticket) {
  const reviewStatus = String(ticket.reviewStatus || '').toLowerCase();
  if (reviewStatus === 'rejected') return 'not valid';
  if (reviewStatus === 'resolved') return 'resolved';
  return ticket.status || reviewStatus || '';
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                   background: s.bg, color: s.fg }}>{s.label}</span>
  );
}

function JobChip({ job, onCancel, busy }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6b7280' }}>
      <StatusBadge status={job.status} />
      {job.status === 'done' && job.resultAppID && (
        <code style={{ fontSize: 10, color: '#166534' }}>{job.resultAppID}</code>
      )}
      {job.status === 'failed' && job.error && (
        <span title={job.error} style={{ fontSize: 10, color: '#991b1b', maxWidth: 280,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.error}</span>
      )}
      <span>{formatDate(job.completedAtMillis || job.requestedAtMillis)}</span>
      {job.status === 'pending' && (
        <button onClick={() => onCancel(job.id)} disabled={busy}
          style={{ fontSize: 10, padding: '1px 6px', border: '1px solid #e5e7eb',
                   background: '#fff', borderRadius: 5, cursor: 'pointer', color: '#6b7280' }}>
          {busy ? '…' : 'Cancel'}
        </button>
      )}
    </div>
  );
}

// A row of toggle chips for one facet (Type / Platform). No selection ⇒ "All"
// is highlighted and every app shows; clicking chips narrows to the union of
// the selected values. Each chip carries its live count.
function FacetChips({ label, order, meta, counts, selected, onToggle, onClear }) {
  const present = order.filter((id) => counts[id]);
  if (present.length === 0) return null;
  const chip = (active, content, onClick, key) => (
    <button key={key} type="button" onClick={onClick} aria-pressed={active}
      style={{ padding: '4px 9px', fontSize: 11, fontWeight: active ? 700 : 500,
               borderRadius: 999, cursor: 'pointer',
               border: `1px solid ${active ? '#111827' : '#e5e7eb'}`,
               background: active ? '#111827' : '#fff', color: active ? '#fff' : '#6b7280' }}>
      {content}
    </button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase',
                     letterSpacing: 0.4 }}>{label}</span>
      {chip(selected.size === 0, 'All', onClear, '__all')}
      {present.map((id) =>
        chip(selected.has(id), `${meta[id].label} ${counts[id]}`, () => onToggle(id), id))}
    </div>
  );
}

const joinTickets = (tickets, selectedIds) =>
  tickets
    .filter((t) => selectedIds.has(t.id))
    .map((t) => t.text.replace(/\s+/g, ' ').trim())
    .join('  AND  ')
    .slice(0, 4000);

export default function AdminFeedbackPage() {
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [busyAction, setBusyAction] = useState('');     // "branch:key" | "cancel:jobId" | ""
  const [goals, setGoals] = useState({});               // key → editable instruction
  const [selected, setSelected] = useState({});         // key → Set(ticketId)
  const [backends, setBackends] = useState({});         // key → 'claude' | 'codex' | 'gemini'
  const [onlyRated, setOnlyRated] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const initialPrefs = useMemo(() => loadPrefs(), []);
  const [sort, setSort] = useState(initialPrefs?.sort || 'newest');
  const [typeFilter, setTypeFilter] = useState(() => new Set(initialPrefs?.types || []));
  const [platformFilter, setPlatformFilter] = useState(() => new Set(initialPrefs?.platforms || []));

  useEffect(() => {
    savePrefs({ sort, types: [...typeFilter], platforms: [...platformFilter] });
  }, [sort, typeFilter, platformFilter]);

  useEffect(() => {
    const off = getAuth().onAuthStateChanged((u) => setUser(u));
    return off;
  }, []);

  const toggleInSet = (setter) => (value) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  const toggleType = toggleInSet(setTypeFilter);
  const togglePlatform = toggleInSet(setPlatformFilter);
  const hasActiveFilters = onlyRated || typeFilter.size > 0 || platformFilter.size > 0;
  const clearAllFilters = () => {
    setTypeFilter(new Set());
    setPlatformFilter(new Set());
    setOnlyRated(false);
  };

  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminListFeedback');
      const result = await fn({ includeClosed: showClosed });
      const list = result.data?.apps || [];
      setApps(list);
      // Seed per-app selection (open tickets) + goal (all joined) once per load.
      const sel = {}; const g = {}; const b = {};
      for (const a of list) {
        const ids = new Set(a.tickets.filter((t) => !ticketIsClosed(t)).map((t) => t.id));
        sel[a.key] = ids;
        g[a.key] = joinTickets(a.tickets, ids);
        b[a.key] = 'claude';
      }
      setSelected(sel); setGoals(g); setBackends(b);
    } catch (err) {
      setError(err.message || 'Load failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) reload();
  }, [isAdmin, showClosed]);   // eslint-disable-line react-hooks/exhaustive-deps

  function toggleTicket(app, ticketId) {
    setSelected((prev) => {
      const next = { ...prev };
      const set = new Set(next[app.key] || []);
      if (set.has(ticketId)) set.delete(ticketId); else set.add(ticketId);
      next[app.key] = set;
      // Regenerate the goal from the new selection (overwrites manual edits —
      // the textarea stays freely editable afterward).
      setGoals((g) => ({ ...g, [app.key]: joinTickets(app.tickets, set) }));
      return next;
    });
  }

  async function handleBranch(app) {
    const goal = (goals[app.key] || '').trim();
    if (!goal) { setStatusMsg('Add a change instruction first.'); return; }
    if (!app.url) { setStatusMsg('This app has no branchable /u/ URL (no publisher uid).'); return; }
    const ids = [...(selected[app.key] || [])];
    if (!window.confirm(`Queue a branch of "${app.name}"? Your Mac will fetch the published app and apply:\n\n${goal.slice(0, 300)}${goal.length > 300 ? '…' : ''}`)) return;
    setBusyAction(`branch:${app.key}`);
    setStatusMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminQueueBranch');
      const result = await fn({
        slug: app.slug || '',
        url: app.url || '',     // the /u/{uid}/{slug}/ inlined-HTML source
        goal,
        productName: app.name,
        productId: app.productId || null,
        feedbackTicketIds: ids,
        backend: backends[app.key] || 'claude',
      });
      setStatusMsg(`Queued ✓ (${result.data?.jobId}). Your Mac will pick it up — watch the status badge.`);
      await reload();
    } catch (err) {
      setStatusMsg(`Error: ${err.message || err}`);
    } finally {
      setBusyAction('');
    }
  }

  async function handleCancel(jobId) {
    setBusyAction(`cancel:${jobId}`);
    try {
      const fn = httpsCallable(getFunctions(), 'adminCancelBranch');
      const result = await fn({ jobId });
      setStatusMsg(result.data?.message || 'Cancelled.');
      await reload();
    } catch (err) {
      setStatusMsg(`Error: ${err.message || err}`);
    } finally {
      setBusyAction('');
    }
  }

  async function handleResolveSelected(app, resolution) {
    const ids = [...(selected[app.key] || [])];
    if (ids.length === 0) {
      setStatusMsg('Select at least one ticket first.');
      return;
    }
    const label = resolution === 'resolved' ? 'resolved/fixed' : 'not valid';
    if (!window.confirm(`Mark ${ids.length} selected feedback ticket${ids.length === 1 ? '' : 's'} for "${app.name}" as ${label}?`)) return;

    setBusyAction(`${resolution}:${app.key}`);
    setStatusMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminResolveFeedbackTickets');
      const result = await fn({ ticketIds: ids, resolution });
      const updated = result.data?.updated || 0;
      const missing = result.data?.missing || [];
      setStatusMsg(`${updated} ticket${updated === 1 ? '' : 's'} marked ${label}.${missing.length ? ` ${missing.length} missing.` : ''}`);
      await reload();
    } catch (err) {
      setStatusMsg(`Error: ${err.message || err}`);
    } finally {
      setBusyAction('');
    }
  }

  async function handleToggleRemoved(app) {
    const productId = app.productDocId || app.productId;
    if (!productId) {
      setStatusMsg('This item does not have a Hub product id.');
      return;
    }
    const willRemove = app.removed !== true;
    if (willRemove) {
      const ok = window.confirm(`Remove "${app.name}" from the public Hub?\n\nThis is the same soft delete as Hub Catalog Cleanup. The /u/ URL keeps working; the Hub listing hides it.`);
      if (!ok) return;
    }

    setBusyAction(`remove:${app.key}`);
    setStatusMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminSetFeedbackProductRemoved');
      const result = await fn({ productId, removed: willRemove });
      setStatusMsg(result.data?.message || (willRemove ? 'Removed from Hub.' : 'Restored to Hub.'));
      await reload();
    } catch (err) {
      setStatusMsg(`Error: ${err.message || err}`);
    } finally {
      setBusyAction('');
    }
  }

  // Enrich each app once with its category facets + ticket time bounds.
  const enriched = useMemo(
    () => apps.map((a) => ({
      app: a,
      type: appType(a),
      platforms: appPlatforms(a),
      times: appTicketTimes(a),
    })),
    [apps]
  );

  // Facet chip counts — computed over the rating filter only (not the facet
  // filters themselves) so each chip shows how many sources it would surface.
  const facetCounts = useMemo(() => {
    const base = onlyRated ? enriched.filter((e) => (e.app.ratingCount || 0) > 0) : enriched;
    const types = {}; const platforms = {};
    for (const e of base) {
      types[e.type] = (types[e.type] || 0) + 1;
      for (const p of e.platforms) platforms[p] = (platforms[p] || 0) + 1;
    }
    return { types, platforms };
  }, [enriched, onlyRated]);

  const visibleApps = useMemo(() => {
    let list = enriched;
    if (onlyRated) list = list.filter((e) => (e.app.ratingCount || 0) > 0);
    if (typeFilter.size) list = list.filter((e) => typeFilter.has(e.type));
    if (platformFilter.size) {
      list = list.filter((e) => [...e.platforms].some((p) => platformFilter.has(p)));
    }
    const sorted = [...list].sort((a, b) => {
      if (sort === 'priority') return b.app.priority - a.app.priority;
      const am = sort === 'oldest' ? a.times.oldest : a.times.newest;
      const bm = sort === 'oldest' ? b.times.oldest : b.times.newest;
      if (am == null && bm == null) return b.app.priority - a.app.priority;
      if (am == null) return 1;          // apps without timestamps sink
      if (bm == null) return -1;
      return sort === 'oldest' ? am - bm : bm - am;
    });
    return sorted.map((e) => e.app);
  }, [enriched, onlyRated, typeFilter, platformFilter, sort]);

  // key → { type, platforms, times } for O(1) lookup while rendering badges.
  const metaByKey = useMemo(() => {
    const m = new Map();
    for (const e of enriched) m.set(e.app.key, e);
    return m;
  }, [enriched]);

  const activeJobs = useMemo(
    () => apps.flatMap((a) => a.jobs || []).filter((j) => j.status === 'pending' || j.status === 'running'),
    [apps]
  );

  if (!user) {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '0 auto', textAlign: 'center', color: '#6b7280' }}>
        <p>Sign in to access admin tools.</p>
        <Link to="/account">Sign in</Link>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '0 auto', textAlign: 'center', color: '#6b7280' }}>
        <p>This page is restricted to administrators.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 64px' }}>
      <header style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 26, color: '#111827' }}>Admin · Feedback → Improve</h1>
        <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 13, maxWidth: 760 }}>
          Community feedback rolled up per app, prioritized by rating × actionable volume.
          Pick the issues to fix, then <strong>Branch &amp; Improve</strong> — your Mac fetches the
          published app, applies the changes with the CLI, and installs a 2.0 for you to review and
          re-publish. Requires mChatAI+ running and signed in as you (the branch poller is on by default).
        </p>
      </header>

      {activeJobs.length > 0 && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb',
                      border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
          <strong>{activeJobs.length}</strong> branch job{activeJobs.length === 1 ? '' : 's'} in flight —
          {' '}{activeJobs.map((j) => j.productName).slice(0, 4).join(', ')}{activeJobs.length > 4 ? '…' : ''}.
          {' '}Keep mChatAI+ open; refresh to see results.
        </div>
      )}

      <nav style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
          {visibleApps.length} source{visibleApps.length === 1 ? '' : 's'} with actionable feedback
        </span>
        <label style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyRated} onChange={(e) => setOnlyRated(e.target.checked)} />
          {' '}Only rated apps
        </label>
        <label style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          {' '}Show closed
        </label>
        <div style={{ flex: 1 }} />
        <Link to="/admin" style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none' }}>← Admin home</Link>
        <button onClick={reload} disabled={loading}
          style={{ padding: '6px 12px', fontSize: 12, background: '#fff',
                   border: '1px solid #e5e7eb', borderRadius: 6, cursor: loading ? 'wait' : 'pointer' }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </nav>

      <div style={{ display: 'flex', gap: 14, marginBottom: 16, alignItems: 'center',
                    flexWrap: 'wrap', rowGap: 8 }}>
        {/* Sort — defaults to reverse-chronological; flip to oldest, or back to
            the rating×volume priority order that drives the branch workflow. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase',
                         letterSpacing: 0.4 }}>Sort</span>
          <div role="radiogroup" aria-label="Sort order"
               style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
            {SORT_OPTIONS.map((o) => {
              const active = sort === o.id;
              return (
                <button key={o.id} type="button" onClick={() => setSort(o.id)}
                  role="radio" aria-checked={active}
                  style={{ padding: '5px 10px', fontSize: 12, fontWeight: active ? 700 : 500,
                           border: 'none', cursor: 'pointer', borderRight: '1px solid #e5e7eb',
                           background: active ? '#111827' : '#fff', color: active ? '#fff' : '#6b7280' }}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Type facet — native applet vs published web mini-app. */}
        {TYPE_ORDER.some((id) => facetCounts.types[id]) && (
          <FacetChips label="Type" order={TYPE_ORDER} meta={TYPE_META}
            counts={facetCounts.types} selected={typeFilter} onToggle={toggleType}
            onClear={() => setTypeFilter(new Set())} />
        )}

        {/* Platform facet — origin of the feedback (iOS / macOS / Web / Android). */}
        {PLATFORM_ORDER.some((id) => facetCounts.platforms[id]) && (
          <FacetChips label="Platform" order={PLATFORM_ORDER} meta={PLATFORM_META}
            counts={facetCounts.platforms} selected={platformFilter} onToggle={togglePlatform}
            onClear={() => setPlatformFilter(new Set())} />
        )}
      </div>

      {error && <div style={{ color: '#991b1b', marginBottom: 12, fontSize: 13 }}>⚠️ {error}</div>}
      {statusMsg && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f9ff',
                      border: '1px solid #bae6fd', borderRadius: 6, fontSize: 13, color: '#075985' }}>
          {statusMsg}
        </div>
      )}

      {loading && apps.length === 0 && <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading feedback…</div>}
      {!loading && visibleApps.length === 0 && (
        hasActiveFilters && apps.length > 0 ? (
          <div style={{ color: '#6b7280', fontSize: 14 }}>
            No feedback matches the current filters.{' '}
            <button onClick={clearAllFilters}
              style={{ fontSize: 13, color: '#3b82f6', background: 'none', border: 'none',
                       padding: 0, cursor: 'pointer', textDecoration: 'underline' }}>
              Clear filters
            </button>
          </div>
        ) : (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>No actionable feedback right now. 🎉</div>
        )
      )}

      {visibleApps.map((app) => {
        const sel = selected[app.key] || new Set();
        const branchBusy = busyAction === `branch:${app.key}`;
        const selectedCount = sel.size;
        const resolveBusy = busyAction === `resolved:${app.key}`;
        const rejectBusy = busyAction === `rejected:${app.key}`;
        const removeBusy = busyAction === `remove:${app.key}`;
        const meta = metaByKey.get(app.key);
        const newestMillis = meta?.times?.newest;
        const sortedTickets = sortTickets(app.tickets, sort);
        return (
          <section key={app.key} style={{ border: '1px solid #e5e7eb', borderRadius: 10,
                    padding: 16, marginBottom: 16, background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#facc15' }}>
                ★{app.rating ? app.rating.toFixed(1) : '–'}
              </span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>({app.ratingCount} ratings)</span>
              <h2 style={{ margin: 0, fontSize: 17, color: '#111827' }}>{app.name}</h2>
              {app.kind === 'applet' && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                               background: '#e0f2fe', color: '#075985' }}>
                  {[app.platformLabel, 'applet'].filter(Boolean).join(' ')}
                </span>
              )}
              {app.kind === 'applet' && app.appletID && (
                <code style={{ fontSize: 10, color: '#64748b' }}>{app.appletID}</code>
              )}
              {app.kind === 'applet' && app.appVersion && (
                <span style={{ fontSize: 10, color: '#94a3b8' }}>v{app.appVersion}</span>
              )}
              {app.kind !== 'applet' && meta?.type && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                               background: '#f1f5f9', color: '#475569' }}>
                  {TYPE_META[meta.type].label}
                </span>
              )}
              {/* Applets already encode their platform in the kind pill above;
                  only show the platform badges for multi-origin mini-apps. */}
              {app.kind !== 'applet' && meta && [...meta.platforms]
                .sort((a, b) => PLATFORM_ORDER.indexOf(a) - PLATFORM_ORDER.indexOf(b))
                .map((p) => (
                  <span key={p} style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                                 background: PLATFORM_META[p].bg, color: PLATFORM_META[p].fg }}>
                    {PLATFORM_META[p].label}
                  </span>
                ))}
              {app.removed === true && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                               background: '#fee2e2', color: '#991b1b' }}>hidden</span>
              )}
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                             background: '#f3f4f6', color: '#6b7280' }}>priority {app.priority.toFixed(1)}</span>
              <div style={{ flex: 1 }} />
              {newestMillis && (
                <span title={`Newest feedback: ${formatDate(newestMillis)}`}
                      style={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {relativeTime(newestMillis)}
                </span>
              )}
              {app.displayUrl && (
                <a href={app.displayUrl} target="_blank" rel="noreferrer"
                   style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none' }}>Open ↗</a>
              )}
            </div>

            {(app.jobs || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10,
                            padding: '8px 10px', background: '#fafafa', borderRadius: 6 }}>
                {app.jobs.map((j) => (
                  <JobChip key={j.id} job={j} onCancel={handleCancel}
                           busy={busyAction === `cancel:${j.id}`} />
                ))}
              </div>
            )}

            <ul style={{ listStyle: 'none', margin: '0 0 12px', padding: 0,
                         display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sortedTickets.map((t) => {
                const cs = clsStyle(t.cls);
                const closed = ticketIsClosed(t);
                const tp = ticketPlatform(t, app.kind);
                return (
                  <li key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, opacity: closed ? 0.72 : 1 }}>
                    <input type="checkbox" checked={sel.has(t.id)}
                           onChange={() => toggleTicket(app, t.id)}
                           style={{ marginTop: 3 }} />
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5,
                                   background: cs.bg, color: cs.fg, flexShrink: 0, marginTop: 1 }}>{cs.label}</span>
                    {tp && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5,
                                     background: PLATFORM_META[tp].bg, color: PLATFORM_META[tp].fg,
                                     flexShrink: 0, marginTop: 1 }}>{PLATFORM_META[tp].label}</span>
                    )}
                    {closed && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5,
                                     background: '#ecfdf5', color: '#166534', flexShrink: 0, marginTop: 1 }}>
                        {ticketStatusLabel(t)}
                      </span>
                    )}
                    {t.contextLabel && app.kind !== 'applet' && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5,
                                     background: '#e0f2fe', color: '#075985', flexShrink: 0, marginTop: 1 }}>
                        {t.contextLabel}
                      </span>
                    )}
                    <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.4, flex: 1, minWidth: 120 }}>{t.text}</span>
                    {t.whenMillis && (
                      <span title={formatDate(t.whenMillis)}
                            style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0, marginTop: 2,
                                     whiteSpace: 'nowrap' }}>
                        {relativeTime(t.whenMillis)}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Change instruction (editable)</label>
            <textarea
              value={goals[app.key] || ''}
              onChange={(e) => setGoals((g) => ({ ...g, [app.key]: e.target.value }))}
              rows={3}
              style={{ width: '100%', marginTop: 4, padding: 8, fontSize: 12, fontFamily: 'inherit',
                       border: '1px solid #e5e7eb', borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#6b7280' }}>{selectedCount} selected</span>
              <button onClick={() => handleResolveSelected(app, 'resolved')} disabled={resolveBusy || selectedCount === 0}
                style={{ padding: '7px 12px', fontSize: 12, fontWeight: 700, borderRadius: 6,
                         border: '1px solid #86efac', cursor: resolveBusy || selectedCount === 0 ? 'not-allowed' : 'pointer',
                         background: '#ecfdf5', color: '#166534' }}>
                {resolveBusy ? 'Saving…' : 'Mark fixed/resolved'}
              </button>
              <button onClick={() => handleResolveSelected(app, 'rejected')} disabled={rejectBusy || selectedCount === 0}
                style={{ padding: '7px 12px', fontSize: 12, fontWeight: 700, borderRadius: 6,
                         border: '1px solid #fdba74', cursor: rejectBusy || selectedCount === 0 ? 'not-allowed' : 'pointer',
                         background: '#fff7ed', color: '#9a3412' }}>
                {rejectBusy ? 'Saving…' : 'Mark not valid'}
              </button>
              <div style={{ flex: 1 }} />
              {app.kind !== 'applet' && (app.productDocId || app.productId) && (
                <button onClick={() => handleToggleRemoved(app)} disabled={removeBusy}
                  style={{ padding: '7px 12px', fontSize: 12, fontWeight: 700, borderRadius: 6,
                           border: `1px solid ${app.removed ? '#86efac' : '#fecaca'}`,
                           cursor: removeBusy ? 'wait' : 'pointer',
                           background: app.removed ? '#ecfdf5' : '#fee2e2',
                           color: app.removed ? '#166534' : '#991b1b' }}>
                  {removeBusy ? 'Saving…' : (app.removed ? 'Restore to Hub' : 'Remove from Hub')}
                </button>
              )}
              <select value={backends[app.key] || 'claude'}
                      onChange={(e) => setBackends((b) => ({ ...b, [app.key]: e.target.value }))}
                      style={{ fontSize: 12, padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                <option value="claude">claude</option>
                <option value="codex">codex</option>
                <option value="gemini">gemini</option>
              </select>
              <button onClick={() => handleBranch(app)} disabled={branchBusy || !app.branchable}
                style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6,
                         border: 'none', cursor: branchBusy ? 'wait' : 'pointer',
                         background: app.branchable ? '#111827' : '#d1d5db', color: '#fff' }}>
                {branchBusy ? 'Queuing…' : 'Branch & Improve →'}
              </button>
              {!app.branchable && (
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  {app.kind === 'applet'
                    ? 'Native applet feedback; fix in the app repo, not via Branch & Improve.'
                    : 'No branchable /u/ URL (no publisher uid).'}
                </span>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
